import React, { useState } from 'react';
import { Panel } from '@app/features/dashboard/components';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import CardWindowNote from './CardWindowNote';
import { formatDayMonth, formatSignedNumber, formatTime } from './clientCard.format';
import { buildVitalSeries, CardVisit, CardWindow, VitalSeries, vitalDelta, vitalLatest } from './clientCard.model';
import VitalChartModal from './VitalChartModal';

const SPARK_WIDTH = 130;
const SPARK_HEIGHT = 34;
const SPARK_COLOR = 'rgb(2 132 199)';

/**
 * The series as one line, with points placed by WHEN they were measured.
 *
 * Evenly spacing them would draw three readings from one week and one from a month
 * later as a steady four-step trend. Vitals are recorded whenever a caregiver
 * happens to take them, so the horizontal axis has to be time.
 */
const Sparkline: React.FC<{ series: VitalSeries }> = ({ series }) => {
  const values = series.points.map((point) => point.value);
  const times = series.points.map((point) => new Date(point.at).getTime());

  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.15 || 1;
  const low = min - pad;
  const high = max + pad;

  const firstTime = times[0];
  const lastTime = times[times.length - 1];
  const span = lastTime - firstTime;

  const x = (time: number) => (span === 0 ? SPARK_WIDTH / 2 : ((time - firstTime) / span) * (SPARK_WIDTH - 4) + 2);
  const y = (value: number) => SPARK_HEIGHT - 3 - ((value - low) / (high - low)) * (SPARK_HEIGHT - 6);

  if (series.points.length === 1) {
    return (
      <svg width={SPARK_WIDTH} height={SPARK_HEIGHT} aria-hidden>
        <circle cx={SPARK_WIDTH / 2} cy={SPARK_HEIGHT / 2} r="2.6" fill={SPARK_COLOR} />
      </svg>
    );
  }

  return (
    <svg width={SPARK_WIDTH} height={SPARK_HEIGHT} viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`} aria-hidden>
      <polyline
        points={series.points.map((point, index) => `${x(times[index])},${y(point.value)}`).join(' ')}
        fill="none"
        stroke={SPARK_COLOR}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx={x(lastTime)} cy={y(values[values.length - 1])} r="2.6" fill={SPARK_COLOR} />
    </svg>
  );
};

const VitalRow: React.FC<{ series: VitalSeries; onOpen: (series: VitalSeries) => void }> = ({ series, onOpen }) => {
  const { t, i18n } = useTranslation();

  const latest = vitalLatest(series);
  const delta = vitalDelta(series);
  const first = series.points[0];

  if (!latest) return null;

  return (
    // The whole row opens the series, because the sparkline is the part a reader
    // reaches for and it is 130px of hairline — too small to be the only target.
    <button
      type="button"
      onClick={() => onOpen(series)}
      className="flex w-full cursor-pointer items-center gap-3 border-0 border-b border-solid border-slate-100 bg-transparent py-2.5 text-left transition-colors last:border-b-0 hover:bg-slate-50"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-medium text-slate-700">{t(series.labelKey)}</div>
        <div className="text-[11px] text-slate-400">
          {[series.unit, t('client-card-vital-measurements', { count: series.points.length })]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </div>

      <Tooltip
        title={t('client-card-vital-latest-tooltip', {
          when: `${formatDayMonth(latest.at, i18n.language)} ${formatTime(latest.at, i18n.language)}`,
          caregiver: latest.caregivers.join(', ') || t('dashboard-note-unassigned-caregiver'),
        })}
      >
        <span className="shrink-0">
          <Sparkline series={series} />
        </span>
      </Tooltip>

      {/* Wide enough for the longest thing the line under the value can say —
          "unchanged since 30/06/2026" — because at 92px, sized when the dates were
          DD/MM, it wrapped onto two lines. */}
      <div className="w-[150px] shrink-0 text-right">
        <div className="text-[15px] font-semibold tabular-nums text-slate-900">
          {new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(latest.value)}
          {series.unit && <span className="ml-1 text-[11px] font-normal text-slate-400">{series.unit}</span>}
        </div>
        {/* Three different things, said three ways. A bare "13/11" left the reader
            guessing what the date was, and "0 since 13/11" read as a value rather
            than as "it has not moved". */}
        <div className="text-[11px] tabular-nums text-slate-500">
          {delta === null
            ? t('client-card-vital-measured-on', { date: formatDayMonth(latest.at, i18n.language) })
            : delta === 0
              ? t('client-card-vital-unchanged-since', { date: formatDayMonth(first.at, i18n.language) })
              : t('client-card-vital-change-since', {
                  change: formatSignedNumber(delta, i18n.language),
                  date: formatDayMonth(first.at, i18n.language),
                })}
        </div>
      </div>
    </button>
  );
};

type VitalsPanelProps = {
  visits: CardVisit[];
  window: CardWindow;
  className?: string;
  bodyMaxHeight?: number;
};

/**
 * The vitals this client's visits recorded, one row each.
 *
 * No target line and no red/green: see CARD_VITALS. The row states what was
 * measured, how many times, and how far it has moved — all facts — and leaves
 * whether that is good news to the person reading it.
 */
const VitalsPanel: React.FC<VitalsPanelProps> = ({ visits, window, className, bodyMaxHeight = 300 }) => {
  const { t } = useTranslation();
  const series = buildVitalSeries(visits);
  const [opened, setOpened] = useState<VitalSeries | null>(null);

  return (
    <Panel
      title={t('client-card-vitals')}
      extra={
        <CardWindowNote
          summary={t('client-card-vital-measurements', {
            count: series.reduce((total, one) => total + one.points.length, 0),
          })}
          window={window}
          lead={t('client-card-vitals-hint')}
        />
      }
      className={className}
      pad="px-4 pb-2"
    >
      {series.length === 0 ? (
        <div className="py-6 text-center text-[13px] text-slate-400">{t('client-card-vitals-empty')}</div>
      ) : (
        // bodyMaxHeight is the basis, not a ceiling: the card stretches to match the care team
        // beside it, and a hard cap left dead space under the rows while hiding rows behind a scrollbar.
        <div className="flex h-full min-h-0 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto" style={{ flexBasis: bodyMaxHeight }}>
            {series.map((one) => (
              <VitalRow key={one.key} series={one} onOpen={setOpened} />
            ))}
          </div>
        </div>
      )}

      <VitalChartModal series={opened} onClose={() => setOpened(null)} />
    </Panel>
  );
};

export default VitalsPanel;
